import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "../lib/config";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { CheckCircle2, Circle, Loader2, Play, Camera, X, Info } from "lucide-react";

// iter183 — Photos may be stored either as base64 `data` (legacy) or as an
// Emergent Object Storage `media_id`. Resolve to a usable <img src>.
function photoSrc(p) {
  if (p?.media_id) return `${API}/media/${p.media_id}`;
  if (p?.url && typeof p.url === "string" && p.url.startsWith("/api/")) {
    return `${API.replace(/\/api$/, "")}${p.url}`;
  }
  return p?.data || "";
}

const STATUS_LABEL = {
  pending:     "Pending",
  on_the_way:  "On the way",
  arrived:     "Arrived",
  in_progress: "In Progress",
  completed:   "Completed",
  skipped:     "Skipped",
};

const PAY_LABEL = {
  unpaid:         "Unpaid",
  pending:        "Payment Pending",
  paid:           "Paid",
  payment_failed: "Charge Failed",
};

const PAY_TONE = {
  unpaid:         "bg-slate-100 text-slate-700 border-slate-200",
  pending:        "bg-amber-100 text-amber-800 border-amber-200",
  paid:           "bg-emerald-100 text-emerald-800 border-emerald-200",
  payment_failed: "bg-red-100 text-red-800 border-red-200",
};

/**
 * iter135 — Per-Service Job Details.
 * Shows each service in a booking as its own Job card with
 * Start / Complete / Photo actions. The property/visit is only
 * "done" when every card here is completed.
 */
export default function JobDetailsList({ bookingId, onUpdated }) {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [invoice, setInvoice] = useState(null);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/bookings/${bookingId}/jobs`);
      setJobs(data.jobs || []);
      setInvoice(data.invoice || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load job details");
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-slate-500 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading jobs…
      </div>
    );
  }

  const total     = Number(invoice?.total || 0);
  const paid      = Number(invoice?.paid || 0);
  const remaining = Number(invoice?.remaining || 0);

  return (
    <div className="space-y-3" data-testid="job-details-list">
      {/* Invoice progress */}
      {invoice && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="flex items-baseline justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-900/60">Visit Invoice</div>
            <div className={`text-[11px] font-extrabold uppercase tracking-wider ${
              invoice.status === "paid" ? "text-emerald-700" :
              invoice.status === "partial" ? "text-amber-700" : "text-slate-500"
            }`} data-testid="invoice-status">
              {invoice.status}
            </div>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl bg-slate-50 p-2">
              <div className="text-[10px] uppercase text-slate-500 font-bold">Total</div>
              <div className="text-base font-black tabular-nums" data-testid="invoice-total">${total.toFixed(2)}</div>
            </div>
            <div className="rounded-xl bg-emerald-50 p-2">
              <div className="text-[10px] uppercase text-emerald-700 font-bold">Paid</div>
              <div className="text-base font-black text-emerald-800 tabular-nums" data-testid="invoice-paid">${paid.toFixed(2)}</div>
            </div>
            <div className="rounded-xl bg-amber-50 p-2">
              <div className="text-[10px] uppercase text-amber-700 font-bold">Due</div>
              <div className="text-base font-black text-amber-800 tabular-nums" data-testid="invoice-remaining">${remaining.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {jobs.map((j) => (
        <JobCard key={j.job_id} job={j} onUpdated={async () => { await load(); onUpdated && onUpdated(); }} />
      ))}
      {jobs.length === 0 && (
        <div className="text-center text-sm text-slate-500 py-4 flex items-center justify-center gap-1.5">
          <Info className="w-4 h-4" /> No jobs — this booking may pre-date the multi-service workflow.
        </div>
      )}
    </div>
  );
}


function JobCard({ job, onUpdated }) {
  const [busy, setBusy] = useState("");
  const beforeRef = useRef(null);
  const afterRef = useRef(null);

  const start = async () => {
    setBusy("start");
    try {
      await api.post(`/admin/jobs/${job.job_id}/start`);
      toast.success(`${job.service_name} started`);
      await onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start job");
    } finally { setBusy(""); }
  };

  const complete = async () => {
    setBusy("complete");
    try {
      const { data } = await api.post(`/admin/jobs/${job.job_id}/complete`, { crew_notes: "" });
      if (data?.autopay_ok) {
        toast.success(`${job.service_name} · charged $${(job.price || 0).toFixed(2)}`);
      } else if (data?.pay_link) {
        toast.success(`${job.service_name} completed · Pay Now link sent`);
      } else {
        toast.success(`${job.service_name} completed`);
      }
      if (data?.all_jobs_completed) {
        toast.success("🎉 All services complete for this visit!");
      }
      await onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete job");
    } finally { setBusy(""); }
  };

  const uploadPhoto = async (file, kind) => {
    if (!file) return;
    setBusy(`photo_${kind}`);
    try {
      // iter183: upload to Emergent Object Storage first, then attach media_id.
      // No client-side size limit — server auto-compresses and stores original.
      const form = new FormData();
      form.append("file", file, file.name || `photo.jpg`);
      let desc;
      let lastErr;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data } = await api.post("/media/upload", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          desc = data;
          break;
        } catch (e) {
          lastErr = e;
          const status = e?.response?.status;
          if (status && status >= 400 && status < 500) throw e;
          if (attempt < 3) await new Promise((r) => setTimeout(r, 600 * attempt));
        }
      }
      if (!desc) throw lastErr;
      await api.post(`/admin/jobs/${job.job_id}/photos`, {
        kind, media_id: desc.media_id,
      });
      toast.success(`${kind === "before" ? "Before" : "After"} photo uploaded`);
      await onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed");
    } finally { setBusy(""); }
  };

  const isCompleted = job.status === "completed";
  const isStarted   = job.status === "in_progress";
  const canStart    = !isCompleted && !isStarted;
  const beforeCount = (job.before_photos || []).length;
  const afterCount  = (job.after_photos || []).length;
  // iter135 — Show the Complete button from the pending state too, disabled
  // until before + after photos are uploaded. Matches the spec wording
  // where both actions are always visible.
  const canShowComplete = !isCompleted;
  const canComplete = canShowComplete && beforeCount > 0 && afterCount > 0;
  const readyLabel  = isCompleted ? "COMPLETED" : (isStarted ? "IN PROGRESS" : "READY");

  return (
    <div
      className={`rounded-2xl border-2 p-3 space-y-3 transition-all ${
        isCompleted ? "bg-emerald-50/50 border-emerald-200" :
        isStarted   ? "bg-amber-50/40 border-amber-200"    :
                      "bg-white border-slate-200"
      }`}
      data-testid={`job-card-${job.job_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isCompleted
              ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
            <span className="font-extrabold text-slate-900 truncate" data-testid={`job-name-${job.job_id}`}>
              {job.service_name}
            </span>
            {job.is_primary && (
              <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 rounded px-1.5 py-0.5">
                PRIMARY
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${
              isCompleted ? "bg-emerald-600 text-white" :
              isStarted   ? "bg-amber-500 text-white"  :
                            "bg-slate-200 text-slate-700"
            }`} data-testid={`job-status-${job.job_id}`}>
              {readyLabel}
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${PAY_TONE[job.payment_status] || PAY_TONE.unpaid}`}
                  data-testid={`job-pay-${job.job_id}`}>
              {PAY_LABEL[job.payment_status] || "Unpaid"}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-lg font-black tabular-nums text-slate-900">${(job.price || 0).toFixed(2)}</div>
          {job.gate_width && (
            <div className="text-[10px] text-slate-500">Gate: {job.gate_width.replace(/_/g, " ")}</div>
          )}
        </div>
      </div>

      {/* Photo counters + upload */}
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="font-bold text-slate-700">Before ({beforeCount})</span>
            <button
              disabled={isCompleted || !!busy}
              onClick={() => beforeRef.current?.click()}
              className="text-emerald-700 font-black text-[10px] disabled:opacity-40 flex items-center gap-1"
              data-testid={`job-photo-before-${job.job_id}`}
            >
              <Camera className="w-3 h-3" /> Add
            </button>
          </div>
          <input ref={beforeRef} type="file" accept="image/*" capture="environment" className="hidden"
                 onChange={(e) => uploadPhoto(e.target.files?.[0], "before")} />
          <div className="flex gap-1 overflow-x-auto">
            {(job.before_photos || []).slice(0, 4).map((p) => (
              <img key={p.photo_id} src={photoSrc(p)} alt="before"
                   className="w-10 h-10 object-cover rounded-md border border-slate-200" loading="lazy" />
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="font-bold text-slate-700">After ({afterCount})</span>
            <button
              disabled={isCompleted || !!busy}
              onClick={() => afterRef.current?.click()}
              className="text-emerald-700 font-black text-[10px] disabled:opacity-40 flex items-center gap-1"
              data-testid={`job-photo-after-${job.job_id}`}
            >
              <Camera className="w-3 h-3" /> Add
            </button>
          </div>
          <input ref={afterRef} type="file" accept="image/*" capture="environment" className="hidden"
                 onChange={(e) => uploadPhoto(e.target.files?.[0], "after")} />
          <div className="flex gap-1 overflow-x-auto">
            {(job.after_photos || []).slice(0, 4).map((p) => (
              <img key={p.photo_id} src={photoSrc(p)} alt="after"
                   className="w-10 h-10 object-cover rounded-md border border-slate-200" loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* Action buttons — Both Start + Complete always visible until done */}
      {!isCompleted && (
        <div className="flex gap-2">
          {canStart && (
            <Button
              onClick={start}
              disabled={!!busy}
              className="flex-1 h-10 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-black text-xs"
              data-testid={`job-start-${job.job_id}`}
            >
              {busy === "start"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Play className="w-4 h-4 mr-1" /> Start Job</>}
            </Button>
          )}
          {canShowComplete && (
            <Button
              onClick={complete}
              disabled={!!busy || !canComplete}
              className={`flex-1 h-10 rounded-full font-black text-xs ${
                canComplete
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}
              data-testid={`job-complete-${job.job_id}`}
              title={!canComplete ? "Upload 1 Before + 1 After photo first" : ""}
            >
              {busy === "complete"
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><CheckCircle2 className="w-4 h-4 mr-1" /> Complete Job</>}
            </Button>
          )}
        </div>
      )}
      {isCompleted && (
        <div className="text-[11px] text-emerald-700 text-center font-semibold">
          ✅ Completed{job.complete_time ? ` at ${new Date(job.complete_time).toLocaleTimeString()}` : ""}
        </div>
      )}
    </div>
  );
}
